/**
 * SPA navigation detection — hooks into History API and polls for URL changes.
 *
 * NOTE: This module exports `onUrlChange` which is also imported by facebook-sdk.ts.
 * The dependency flows: facebook-sdk → spa-navigation (no circular dep).
 */

import { state, debugLog } from "./state.js";
import { checkAndFireEvent } from "./config.js";
import { retagClickRules } from "./element-tagging.js";
import { DEBOUNCE_DELAY_MS, POLLING_INTERVAL_MS } from "./constants.js";

/** Hash-routing SPAs (#/route, #!route) navigate via the hash; a plain
 * #section hash is an anchor scroll on the SAME page. */
function isRouteLikeHash(hash: string): boolean {
  return hash.indexOf("#/") === 0 || hash.indexOf("#!") === 0;
}

/** Strip anchor hashes so a scroll-to-section never counts as navigation;
 * route-like hashes stay (they ARE the page for hash-routers). Without this
 * every "Bekijk hoe het werkt"-style anchor click produced a full PageView
 * to every platform (found live on rechtswijs.nl, 2026-07-23). */
function stripAnchorHash(url: string): string {
  const hashIndex = url.indexOf("#");
  if (hashIndex === -1) return url;
  if (isRouteLikeHash(url.slice(hashIndex))) return url;
  return url.slice(0, hashIndex);
}

function onUrlChangeInner(): void {
  const currentUrl = window.location.href;
  debugLog("onUrlChange", "currentUrl=" + currentUrl, "lastTracked=" + state.lastTrackedUrl, "hasConfig=" + !!state.config);
  if (currentUrl === state.lastTrackedUrl) return;
  const anchorOnlyChange =
    state.lastTrackedUrl !== "" &&
    stripAnchorHash(currentUrl) === stripAnchorHash(state.lastTrackedUrl);
  state.lastTrackedUrl = currentUrl;
  if (anchorOnlyChange) return;

  if (!state.initialized) return;

  if (!state.config) {
    debugLog("onUrlChange", "no config yet, queuing to pendingUrls");
    state.pendingUrls.push(currentUrl);
    return;
  }

  if (state.config.pixelId && !state.sdkResolved) {
    debugLog("onUrlChange", "SDK not resolved yet, queuing to pendingUrls");
    state.pendingUrls.push(currentUrl);
    return;
  }

  checkAndFireEvent(currentUrl);
  retagClickRules();
}

function debouncedUrlChange(): void {
  if (state.debounceTimer) clearTimeout(state.debounceTimer);
  state.debounceTimer = setTimeout(() => {
    state.debounceTimer = null;
    onUrlChangeInner();
  }, DEBOUNCE_DELAY_MS);
}

/** All URL change sources go through debounced handler. */
export function onUrlChange(): void {
  debouncedUrlChange();
}

/** Wrap history.pushState and history.replaceState. */
export function installHistoryWrappers(): void {
  history.pushState = function () {
    state.originalPushState.apply(history, arguments as unknown as Parameters<History["pushState"]>);
    onUrlChange();
  };
  history.replaceState = function () {
    state.originalReplaceState.apply(history, arguments as unknown as Parameters<History["replaceState"]>);
    onUrlChange();
  };
}

/** Start polling fallback for hash changes and frameworks that bypass History API. */
export function startPolling(): void {
  state.pollingUrl = window.location.href;
  setInterval(() => {
    const currentUrl = window.location.href;
    if (currentUrl !== state.pollingUrl) {
      state.pollingUrl = currentUrl;
      onUrlChange();
    }
  }, POLLING_INTERVAL_MS);
}
